import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { getPreviousPageUrl } from '@src/quince-site-common/src/utils/getPreviousPageUrl'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { getCookie } from '@src/quince-site-integrated/src/utils'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'

import { TELEMETRY_EVENT_NAMES } from '../telemetry-events.constants'
import { TelemetryIntegration } from '../telemetry-events.types'

import {
  formatRevenue,
  formatValue,
  getSHA1Hashed,
  getSHA256Hashed,
  removeHyphensFromEventId,
} from './provider-utils/data-layer-pixel-utils'
import { trackPixelWithSGTM } from './server-pixel'

export const ServerSGTMPixel: TelemetryIntegration = {
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
  },
  events: {
    [TELEMETRY_EVENT_NAMES.ADD_TO_CART]: async (properties: any) => {
      const eventId = properties?.eventId?.toString() ?? ''
      const alphanumericEventId = removeHyphensFromEventId(eventId)
      const emailHashed256 = await getSHA256Hashed(
        getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ?? getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL),
      )
      const lbUserId = getlbUserId()?.trim() ?? ''
      const lbUserIdSHA256 = await getSHA256Hashed(lbUserId)
      const pageUrl = window.location.href

      const localisationData = getLocalisationDataFromStorage()

      trackPixelWithSGTM({
        eventId,
        alphanumericEventId,
        eventType: 'add_to_cart',
        eventTimestamp: Date.now(),
        productId: properties?.productId?.toString(),
        itemName: `${properties?.productTitle?.toString() ?? ''} - ${properties?.variantTitle?.toString() ?? ''}`,
        productType: properties?.productType?.toString() ?? '',
        currency: localisationData?.currency,
        country: localisationData?.country,
        language: localisationData?.language,
        locale: localisationData?.locale,
        price: formatRevenue(properties?.price),
        value: formatValue(properties?.price, properties?.quantity),
        quantity: properties?.quantity ?? 1,
        brand: properties?.vendor ?? 'Quince',
        emailSha256: emailHashed256 ?? '',
        lbUserIdSHA256,
        pageUrl,
      })
    },
    [TELEMETRY_EVENT_NAMES.PRODUCT_VIEW]: async (properties: any) => {
      const eventId = properties?.eventId?.toString() ?? ''
      const alphanumericEventId = removeHyphensFromEventId(eventId)
      const emailHashed256 = await getSHA256Hashed(
        getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ?? getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL),
      )
      const lbUserId = getlbUserId()?.trim() ?? ''
      const lbUserIdSHA256 = await getSHA256Hashed(lbUserId)
      const pageUrl = window.location.href
      const localisationData = getLocalisationDataFromStorage()
      trackPixelWithSGTM({
        eventId,
        alphanumericEventId,
        eventType: 'product_view',
        eventTimestamp: Date.now(),
        productId: properties?.productId?.toString(),
        itemName: `${properties?.productTitle?.toString() ?? ''} - ${properties?.variantTitle?.toString() ?? ''}`,
        productType: properties?.productType?.toString() ?? '',
        currency: localisationData?.currency,
        country: localisationData?.country,
        language: localisationData?.language,
        locale: localisationData?.locale,
        price: formatRevenue(properties?.price),
        value: formatValue(properties?.price, properties?.quantity),
        quantity: properties?.quantity ?? 1,
        brand: properties?.vendor ?? 'Quince',
        emailSha256: emailHashed256 ?? '',
        lbUserIdSHA256,
        pageUrl,
      })
    },
    [TELEMETRY_EVENT_NAMES.PAGE_VIEW]: async (properties: any) => {
      const hashedEmail = await getSHA1Hashed(
        getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ?? getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL),
      )
      const impactClickId = getCookie(ANALYTIC_COOKIES.IMPACT_CLICK_ID) ?? ''
      const lbUserId = getlbUserId()?.trim() ?? ''
      const eventId = properties?.eventId?.toString() ?? ''
      const alphanumericEventId = removeHyphensFromEventId(eventId)
      const pageUrl = window.location.href
      const referrer = (isBrowser() && document.referrer) || ''
      const previousPageUrl = getPreviousPageUrl()
      const date = new Date()
      const localisationData = getLocalisationDataFromStorage()
      trackPixelWithSGTM(
        {
          eventId,
          alphanumericEventId,
          eventType: 'page_view',
          eventTimestamp: date.getTime(),
          eventTimestampISO: date.toISOString(),
          email: hashedEmail ?? '',
          impactClickId,
          lbUserId,
          pageUrl,
          referrer,
          previousPageUrl,
          currency: localisationData?.currency,
          country: localisationData?.country,
          language: localisationData?.language,
          locale: localisationData?.locale,
        },
        true,
      )
    },
    [TELEMETRY_EVENT_NAMES.COLLECTION_VIEW]: async properties => {
      const eventId = properties.eventId.toString() || ''
      const date = new Date()
      const emailHashed256 = await getSHA256Hashed(
        getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ?? getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL),
      )
      const productIdsInString = Array.from(new Set(properties.variantList.map(x => x.product_id.toString())))
      const lbUserId = getlbUserId()?.trim() ?? ''
      const lbUserIdSHA256 = await getSHA256Hashed(lbUserId)
      const pageUrl = window.location.href
      const localisationData = getLocalisationDataFromStorage()
      trackPixelWithSGTM({
        eventId,
        eventType: 'collection_view',
        eventTimestamp: date.getTime(),
        emailSha256: emailHashed256 ?? '',
        productIdsInString,
        collectionName: properties.collectionTitle,
        currency: localisationData?.currency,
        country: localisationData?.country,
        language: localisationData?.language,
        locale: localisationData?.locale,
        lbUserIdSHA256,
        pageUrl,
      })
    },
    [TELEMETRY_EVENT_NAMES.SEARCH]: async (properties: any) => {
      const eventId = properties?.eventId?.toString() || ''
      const date = new Date()
      const emailHashed256 = await getSHA256Hashed(
        getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ?? getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL),
      )
      const lbUserId = getlbUserId()?.trim() ?? ''
      const lbUserIdSHA256 = await getSHA256Hashed(lbUserId)
      const pageUrl = window.location.href
      const localisationData = getLocalisationDataFromStorage()
      trackPixelWithSGTM({
        eventId,
        // eslint-disable-next-line i18next/no-literal-string
        eventType: 'search',
        eventTimestamp: date.getTime(),
        emailSha256: emailHashed256 ?? '',
        searchString: properties?.searchQuery,
        lbUserIdSHA256,
        pageUrl,
        currency: localisationData?.currency,
        country: localisationData?.country,
        language: localisationData?.language,
        locale: localisationData?.locale,
      })
    },
  },
}
